// One lesson inside the Academy frame: the living player at the top, a
// sticky "try it" action card (walkthrough + sample), the title block, "What
// you will learn", the body, the sample download card, mark as completed,
// previous/next cards, and related lessons. On xl a slim "On this page" list
// of the body's headings sits to the right.
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, CheckCircleIcon, CursorArrowRaysIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import Button from "../../components/common/Button";
import DownloadCard, { downloadFile } from "../../components/common/DownloadCard";
import { createRevealSequence } from "../../components/common/Reveal";
import Tooltip from "../../components/common/Tooltip";
import Toggle from "../../components/Settings/Toggle";
import { useAuth } from "../../context/AuthContext";
import { adjacentLessons, getCourse, getLesson, lessonsForCourse } from "../catalog";
import { LessonDefinition } from "../catalog/types";
import { useAcademyProgress } from "../progress";
import { getSceneById } from "../scenes";
import { SAMPLES_BASE } from "../scenes/types";
import { openWalkthrough, walkthroughAvailable } from "../walkthrough";
import { DoneChip, GateChip, MinutesChip, NewChip, PlatformChip } from "./Chip";
import { useGateStatus } from "./gates";
import LessonBody, { headingAnchors } from "./LessonBody";
import LessonCard, { isNewForUser } from "./LessonCard";
import LessonPlayer from "./LessonPlayer";

function relatedLessons(lesson: LessonDefinition): LessonDefinition[] {
  const explicit = (lesson.related ?? []).map((id) => getLesson(id)).filter((l): l is LessonDefinition => Boolean(l) && l!.id !== lesson.id);
  if (explicit.length > 0) return explicit.slice(0, 3);
  const list = lessonsForCourse(lesson.courseId);
  const i = list.findIndex((l) => l.id === lesson.id);
  return list.slice(i + 1, i + 3);
}

const LessonPage: React.FC = () => {
  const { courseId = "", lessonId = "" } = useParams();
  const lesson = getLesson(lessonId);
  const course = getCourse(courseId);
  const { user } = useAuth();
  const { markViewed, toggleCompleted, isCompleted, isViewed } = useAcademyProgress();
  const gate = useGateStatus(lesson?.gate);
  const scene = useMemo(() => (lesson?.sceneId ? getSceneById(lesson.sceneId) : undefined), [lesson?.sceneId]);

  useEffect(() => {
    if (lesson) markViewed(lesson.id);
  }, [lesson, markViewed]);

  if (!lesson || !course || lesson.courseId !== course.id) return <Navigate to={course ? `/academy/${course.id}` : "/academy"} replace />;

  const { prev, next } = adjacentLessons(lesson.id);
  const related = relatedLessons(lesson);
  const done = isCompleted(lesson.id);
  const tourReady = walkthroughAvailable(lesson.tourId);
  const walkthroughLocked = Boolean(gate?.locked);
  const isNew = isNewForUser(lesson, (user as any)?.created_at, isViewed(lesson.id));
  const anchors = headingAnchors(lesson.body);
  const enter = createRevealSequence();
  const slot = (extra?: string) => {
    const r = enter();
    return { className: cn(r.className, extra), style: r.style };
  };

  const walkthroughButton = (
    <Button variant="primary" size="sm" className="px-3" disabled={walkthroughLocked} onClick={() => lesson.tourId && openWalkthrough(lesson.tourId)} title="Opens the app in a new tab">
      <CursorArrowRaysIcon className="size-4" /> Start the walkthrough <ArrowTopRightOnSquareIcon className="size-3.5 opacity-70" />
    </Button>
  );

  const actionCard = (
    <div {...slot("sticky top-16 z-10 -mx-1 px-1 pt-3 -mt-2 bg-base-100")}>
      <div className="rounded-lg border border-base-300 bg-base-100 shadow-sm p-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[12rem]">
          <p className="m-0 text-sm font-medium">Try it yourself</p>
          <p className="m-0 text-xs text-base-content/60">
            {tourReady ? (walkthroughLocked && gate ? gate.reason : "Opens the app in a new tab and guides you step by step in your own account. Keep this lesson open to read along.") : "A live walkthrough for this lesson is coming soon. The written steps below cover it."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lesson.sample && (
            <Button variant="secondary" size="sm" className="px-3" onClick={() => downloadFile(`${SAMPLES_BASE}/${lesson.sample!.file}`, lesson.sample!.file)}>
              <ArrowDownTrayIcon className="size-4" /> Download sample
            </Button>
          )}
          {tourReady ? (
            walkthroughLocked && gate ? (
              <Tooltip overlay={gate.reason} placement="top">
                <span className="inline-block">{walkthroughButton}</span>
              </Tooltip>
            ) : (
              walkthroughButton
            )
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-base-content/50">
              <CursorArrowRaysIcon className="size-4" /> Walkthrough coming soon
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* The player spans the whole main column (not the 3xl reading width):
          it is a scaled 1440px app viewport, so every pixel of width goes
          straight to legibility. */}
      {scene && (
        <div {...slot()}>
          <LessonPlayer scene={scene} lessonTitle={lesson.title} />
        </div>
      )}

    <div className={cn("gap-8", anchors.length >= 2 && "xl:grid xl:grid-cols-[minmax(0,1fr)_11rem]")}>
      <div className="min-w-0 space-y-6 max-w-3xl">
        {actionCard}

        <header {...slot()}>
          <h1 className="text-3xl font-semibold">{lesson.title}</h1>
          {lesson.subtitle && <p className="text-lg text-base-content/60 mt-1 m-0">{lesson.subtitle}</p>}
          <p className="text-base-content/70 text-sm mt-1 m-0">{lesson.summary}</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <MinutesChip minutes={lesson.minutes} />
            {lesson.platform && <PlatformChip platform={lesson.platform} />}
            {gate && <GateChip label={gate.label} locked={gate.locked} reason={gate.reason} />}
            {isNew && <NewChip />}
            {done && <DoneChip />}
          </div>
        </header>

        {lesson.learn.length > 0 && (
          <section {...slot("rounded-lg border border-base-300 bg-base-100 p-4")}>
            <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-base-content/60">What you will learn</h2>
            <ul className="mt-2 space-y-1.5 text-sm text-base-content/80 m-0 p-0 list-none">
              {lesson.learn.map((line, i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircleIcon className="size-4 mt-0.5 shrink-0 text-blue-500" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <article>
          <LessonBody blocks={lesson.body} reveal={enter} withAnchors />
        </article>

        {lesson.sample && (
          <div {...slot()} id="sample">
            <DownloadCard
              href={`${SAMPLES_BASE}/${lesson.sample.file}`}
              fileName={lesson.sample.file}
              title={lesson.sample.title}
              description={lesson.sample.description}
              length={lesson.sample.length}
              whatToExpect={lesson.sample.whatToExpect}
            />
          </div>
        )}

        <div {...slot("rounded-lg border border-base-300 bg-base-100 p-3")}>
          <Toggle layout="compact" label="Mark as completed" description="Counts toward the course progress and the ring in the navigator." checked={done} onChange={() => toggleCompleted(lesson.id)} id={`lesson-done-${lesson.id}`} />
        </div>

        {(prev || next) && (
          <nav {...slot("border-t border-base-300 pt-6")} aria-label="Lesson navigation">
            <div className="grid gap-3 md:grid-cols-2">
              {prev ? <LessonCard lesson={prev} eyebrow="Previous" /> : <span className="hidden md:block" />}
              {next && <LessonCard lesson={next} eyebrow="Next" />}
            </div>
          </nav>
        )}

        {related.length > 0 && (
          <section {...slot("border-t border-base-300 pt-6")}>
            <h2 className="m-0 mb-3 text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider">Related lessons</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {related.map((l) => (
                <LessonCard key={l.id} lesson={l} showCourse />
              ))}
            </div>
          </section>
        )}
      </div>

      {anchors.length >= 2 && (
        <aside className="hidden xl:block self-start sticky top-20 pt-1">
          <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wider text-base-content/50">On this page</p>
          <ul className="m-0 p-0 list-none space-y-1 border-l border-base-300">
            {anchors.map((a) => (
              <li key={a.id}>
                <a href={`#${a.id}`} className="block pl-3 -ml-px border-l border-transparent text-xs text-base-content/60 hover:text-blue-600 dark:hover:text-blue-300 hover:border-blue-500 leading-snug py-0.5">
                  {a.text}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
    </div>
  );
};

export default LessonPage;
